import { ButtonHTMLAttributes, ElementType, ReactNode } from 'react';
import styled, { css } from 'styled-components';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  leftIcon?: ReactNode;
  as?: ElementType;
  href?: string;
  target?: string;
  rel?: string;
  type?: 'button' | 'submit' | 'reset';
}

const sizes = {
  sm: css`
    height: 34px;
    padding: 0 14px;
    font-size: 13px;
  `,
  md: css`
    height: 40px;
    padding: 0 18px;
    font-size: 15px;
  `,
  lg: css`
    height: 46px;
    padding: 0 22px;
    font-size: 15px;
  `,
};

const variants = {
  primary: css`
    background: var(--screening-navy);
    color: #fff;
    border: 1px solid var(--screening-navy);
    &:hover:not(:disabled) {
      background: var(--screening-navy-deep);
      border-color: var(--screening-navy-deep);
    }
  `,
  secondary: css`
    background: var(--screening-paper);
    color: var(--screening-ink);
    border: 1px solid var(--screening-hairline);
    &:hover:not(:disabled) {
      border-color: var(--screening-navy);
    }
  `,
  ghost: css`
    background: transparent;
    color: var(--screening-muted);
    border: 1px solid transparent;
    &:hover:not(:disabled) {
      color: var(--screening-ink);
    }
  `,
  danger: css`
    background: var(--screening-reject-wash);
    color: var(--screening-reject);
    border: 1px solid var(--screening-reject-wash);
    &:hover:not(:disabled) {
      border-color: var(--screening-reject);
    }
  `,
};

const StyledButton = styled.button<{
  $variant: Variant;
  $size: Size;
  $fullWidth?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: var(--screening-font-ui);
  font-weight: 600;
  border-radius: var(--screening-r-button);
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 160ms ease,
    border-color 160ms ease,
    color 160ms ease;
  text-decoration: none;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid var(--screening-navy);
    outline-offset: 2px;
  }

  ${({ $size }) => sizes[$size]};
  ${({ $variant }) => variants[$variant]};
`;

const Button = ({
  variant = 'primary',
  size = 'md',
  fullWidth,
  leftIcon,
  children,
  type = 'button',
  ...rest
}: ButtonProps) => (
  <StyledButton
    $variant={variant}
    $size={size}
    $fullWidth={fullWidth}
    type={type}
    {...rest}
  >
    {leftIcon}
    {children}
  </StyledButton>
);

export default Button;
